<script lang="ts">
  import DashHero from '../shared/DashHero.svelte';
  import KpiCard from '../widgets/KpiCard.svelte';
  import QuickActions from '../widgets/QuickActions.svelte';
  import DashCard from '../shared/DashCard.svelte';
  import { Veterinaria, Citas } from '../../../lib/api';
  import { tenantCtx } from '../../../lib/tenant.svelte';

  let { onNavigate }: { onNavigate: (tab: string) => void } = $props();
  const showPlus = $derived(tenantCtx.plan === 'clinica-plus' || tenantCtx.edicion === 'clinic-network');

  let mascotas = $state(0);
  let internados = $state(0);
  let jaulasLibres = $state(0);
  let citasHoy = $state(0);
  let productosAlimento = $state(0);
  let ventasDia = $state(0);
  let agenda = $state<Array<{ hora: string; paciente: string; motivo: string }>>([]);
  let internamientoLista = $state<Array<{ mascota: string; jaula: string; motivo: string }>>([]);

  function hoy() { return new Date().toISOString().slice(0, 10); }

  async function cargar() {
    try {
      const [ms, ints, jaulas, citas, prods] = await Promise.all([
        Veterinaria.mascotas().catch(() => []),
        Veterinaria.internamientosActivos().catch(() => []),
        Veterinaria.jaulas().catch(() => []),
        Citas.listar(hoy()).catch(() => []),
        Veterinaria.productos('ALIMENTO').catch(() => []),
      ]);
      mascotas = ms.length;
      internados = ints.length;
      jaulasLibres = jaulas.filter(j => j.estado === 'DISPONIBLE').length;
      citasHoy = citas.length;
      productosAlimento = prods.length;

      const mascotaMap = new Map(ms.map(m => [m.id, m.nombre]));
      const jaulaMap = new Map(jaulas.map(j => [j.id, j.numero]));
      internamientoLista = ints.slice(0, 5).map(i => ({
        mascota: mascotaMap.get(i.mascotaId!) ?? 'Mascota',
        jaula: jaulaMap.get(i.jaulaId!) ?? '—',
        motivo: i.motivo ?? 'Observación',
      }));

      agenda = citas.slice(0, 6).map(c => ({
        hora: c.fechaHora
          ? new Date(c.fechaHora).toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit' })
          : '09:00',
        paciente: c.pacienteNombre ?? 'Consulta',
        motivo: c.motivo ?? 'Control',
      }));
    } catch {
    }
  }

  $effect(() => { void cargar(); });

  const actions = [
    { id: 'veterinaria', label: 'Nueva mascota', icon: '🐶' },
    { id: 'expedienteVet', label: 'Expediente mascota', icon: '📋' },
    { id: 'vetInternamiento', label: 'Internar', icon: '🏥' },
    { id: 'vetMostrador', label: 'Vender alimento', icon: '🦴' },
    { id: 'vetFarmacia', label: 'Farmacia vet', icon: '💊' },
    { id: 'agenda', label: 'Agenda', icon: '📅' },
  ];
</script>

<DashHero emoji="" title="Centro veterinario" subtitle="Mascotas, internamiento, tienda y farmacia en un solo lugar" variant="vet" showPlus={showPlus} />

<QuickActions {actions} onAction={onNavigate} />

<div class="dash-grid">
  <div class="col-3"><KpiCard label="Mascotas activas" value={mascotas} hint="Fichas registradas" tone="brand" /></div>
  <div class="col-3"><KpiCard label="Internados hoy" value={internados} hint="{jaulasLibres} jaulas libres" tone="warning" /></div>
  <div class="col-3"><KpiCard label="Citas hoy" value={citasHoy} hint="Consultas programadas" tone="success" /></div>
  <div class="col-3"><KpiCard label="Ventas mostrador" value={"Q " + ventasDia} hint="{productosAlimento} SKUs alimento" tone="total" /></div>

  <div class="col-6">
    <DashCard title="Agenda de hoy" hint="Consultas y procedimientos">
      <ul class="timeline">
        {#each agenda as a}
          <li class="tl-item"><strong>{a.hora}</strong> · {a.paciente}<span>{a.motivo}</span></li>
        {/each}
      </ul>
    </DashCard>
  </div>

  <div class="col-6">
    <DashCard title="Internamiento activo" hint="Pacientes hospitalizados">
      <table class="mini-table">
        <thead><tr><th>Mascota</th><th>Jaula</th><th>Motivo</th></tr></thead>
        <tbody>
          {#each internamientoLista as row}
            <tr><td>{row.mascota}</td><td>{row.jaula}</td><td>{row.motivo}</td></tr>
          {/each}
        </tbody>
      </table>
    </DashCard>
  </div>
</div>

<style>
  .timeline { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.55rem; }
  .tl-item {
    padding: 0.6rem 0.75rem;
    border-radius: var(--radius-lg);
    background: rgba(255,255,255,0.45);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    border: 1px solid var(--glass-border);
    font-size: 0.88rem;
    transition: box-shadow 0.2s ease, transform 0.2s ease;
    color: var(--text-primary);
  }
  .tl-item:hover {
    box-shadow: var(--shadow-md);
    transform: translateX(2px);
  }
  .tl-item strong { font-weight: 700; color: var(--brand-deep); }
  .tl-item span { display: block; font-size: 0.78rem; color: var(--text-secondary); margin-top: 0.15rem; }
  .mini-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
  .mini-table th, .mini-table td { text-align: left; padding: 0.5rem 0.6rem; border-bottom: 1px solid var(--glass-border); }
  .mini-table th { color: var(--text-secondary); font-weight: 700; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.03em; background: rgba(255,255,255,0.3); }
  .mini-table tr:hover td { background: rgba(255,255,255,0.35); }
</style>
